/**
 * Detects lazy-chunk fetch failures after a deploy replaces hashed filenames.
 * Production Angular/esbuild uses "Failed to fetch dynamically imported module";
 * webpack used "Loading chunk … failed" / ChunkLoadError.
 */
const CHUNK_LOAD_MARKERS = [
	'Failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'Importing a module script failed',
	// WebKit when SPA server returns index.html for a missing hashed chunk
	'is not a valid JavaScript MIME type',
	'ChunkLoadError',
];

export function isChunkLoadFailure(error: unknown): boolean {
	return collectErrorTexts(error).some(textMatchesChunkLoad);
}

/**
 * Safari/iOS surfaces failed dynamic imports via `window` `error` / `unhandledrejection`
 * before Angular ErrorHandler runs. Register once at bootstrap.
 */
export function registerChunkLoadGlobalHandlers(
	onChunkLoadFailure: (error: unknown) => void
): () => void {
	const handleWindowError = (event: ErrorEvent): void => {
		const error = event.error ?? event.message;
		if (!isChunkLoadFailure(error) && !isChunkLoadFailure(event.message)) {
			return;
		}

		event.preventDefault();
		onChunkLoadFailure(error);
	};

	const handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
		if (!isChunkLoadFailure(event.reason)) {
			return;
		}

		event.preventDefault();
		onChunkLoadFailure(event.reason);
	};

	window.addEventListener('error', handleWindowError);
	window.addEventListener('unhandledrejection', handleUnhandledRejection);

	return () => {
		window.removeEventListener('error', handleWindowError);
		window.removeEventListener(
			'unhandledrejection',
			handleUnhandledRejection
		);
	};
}

/** Isolated so tests can spy without redefining `window.location`. */
export function reloadAppWindow(): void {
	window.location.reload();
}

function collectErrorTexts(error: unknown, depth = 0): string[] {
	if (error == null || depth > 4) {
		return [];
	}

	if (typeof error === 'string') {
		return [error];
	}

	if (typeof error !== 'object') {
		return [String(error)];
	}

	if (typeof ErrorEvent !== 'undefined' && error instanceof ErrorEvent) {
		const message =
			typeof error.message === 'string' ? [error.message] : [];
		return message.concat(collectErrorTexts(error.error, depth + 1));
	}

	const record = error as {
		name?: unknown;
		message?: unknown;
		originalError?: unknown;
		cause?: unknown;
		rejection?: unknown;
	};

	const texts: string[] = [];
	if (typeof record.name === 'string' && record.name.length > 0) {
		texts.push(record.name);
	}
	if (typeof record.message === 'string' && record.message.length > 0) {
		texts.push(record.message);
	}

	return texts.concat(
		collectErrorTexts(record.originalError, depth + 1),
		collectErrorTexts(record.cause, depth + 1),
		collectErrorTexts(record.rejection, depth + 1)
	);
}

function textMatchesChunkLoad(text: string): boolean {
	for (const marker of CHUNK_LOAD_MARKERS) {
		if (text.includes(marker)) {
			return true;
		}
	}

	const loadingChunkIndex = text.indexOf('Loading chunk');
	if (loadingChunkIndex === -1) {
		return false;
	}

	return text.indexOf(' failed', loadingChunkIndex) !== -1;
}
